import React from 'react';

interface UseTypewriterOptions {
  text: string;
  enabled: boolean;
  signal?: AbortSignal;
  delay?: number;
  onComplete?: () => void;
}

interface UseTypewriterResult {
  displayedText: string;
  isComplete: boolean;
}

export function useTypewriter({
  text,
  enabled,
  signal,
  delay = 17,
  onComplete,
}: UseTypewriterOptions): UseTypewriterResult {
  const [displayedText, setDisplayedText] = React.useState<string>('');
  const [isComplete, setIsComplete] = React.useState<boolean>(false);

  // Refs for values that should NOT restart the effect when they change.
  // This prevents Vite HMR from killing the async loop when the parent
  // re-renders with new callback / AbortSignal references.
  const onCompleteRef = React.useRef(onComplete);
  onCompleteRef.current = onComplete;

  React.useEffect(() => {
    if (!enabled) {
      setDisplayedText('');
      setIsComplete(false);
      return;
    }

    const currentSignal = signal;

    // If the signal is already aborted (e.g. parent cleanup during HMR),
    // show the full text immediately instead of freezing mid-animation.
    if (currentSignal?.aborted) {
      setDisplayedText(text);
      setIsComplete(true);
      onCompleteRef.current?.();
      return;
    }

    let cancelled = false;

    const abortHandler = () => {
      cancelled = true;
    };
    currentSignal?.addEventListener('abort', abortHandler);

    const run = async () => {
      // If we are already complete or partially complete, reset gracefully if starting over,
      // but typically we just type character by character.
      for (let i = 0; i < text.length; i++) {
        if (cancelled) return;
        setDisplayedText(text.slice(0, i + 1));
        await new Promise<void>((r) => setTimeout(r, delay));
      }
      if (!cancelled) {
        setIsComplete(true);
        onCompleteRef.current?.();
      }
    };

    run();

    return () => {
      cancelled = true;
      currentSignal?.removeEventListener('abort', abortHandler);
    };
  }, [text, enabled, delay, signal]);

  return { displayedText, isComplete };
}
